import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Zap,
  Globe,
  Database,
  Copy,
  ExternalLink,
  Sparkles,
  AlertCircle,
  Terminal,
  Brain,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { ThinkingStep } from '../types'

export interface ReasoningAccordionProps {
  steps: ThinkingStep[]
  isStreaming?: boolean
  thoughtDuration?: number
  className?: string
}

export interface GroundingSource {
  id: string
  title: string
  url?: string
  type: 'web' | 'crm' | 'doc' | 'other'
  snippet?: string
  entityType?: string
}

const TOOL_LABEL_FALLBACKS: Record<string, string> = {
  hubspot_search_contacts: 'Searching CRM contacts',
  hubspot_get_contact: 'Fetching contact details',
  hubspot_create_contact: 'Creating CRM contact',
  hubspot_update_contact: 'Updating CRM contact',
  hubspot_search_deals: 'Searching deals',
  hubspot_get_deal: 'Fetching deal details',
  hubspot_create_deal: 'Creating deal',
  hubspot_update_deal: 'Updating deal',
  hubspot_log_note: 'Logging note to CRM',
  hubspot_create_task: 'Creating CRM task',
  web_search: 'Searching web news',
  qualify_prospect: 'Calculating ICP score',
  gmail_search_threads: 'Searching email threads',
  gmail_get_email: 'Reading email',
  gmail_send_email: 'Sending email',
  gmail_reply_to_email: 'Sending reply',
}

function resolveToolLabel(step: ThinkingStep): string {
  if (step.label && step.label.trim()) return step.label
  if (TOOL_LABEL_FALLBACKS[step.toolName]) return TOOL_LABEL_FALLBACKS[step.toolName]
  return step.toolName ? step.toolName.replace(/_/g, ' ') : 'Tool execution'
}

function formatDuration(
  thoughtDuration?: number,
  steps?: ThinkingStep[],
  measuredElapsed?: number | null
): string {
  // 1. Explicit thoughtDuration passed in
  if (typeof thoughtDuration === 'number' && thoughtDuration > 0) {
    const sec = thoughtDuration > 100 ? thoughtDuration / 1000 : thoughtDuration
    return `${sec.toFixed(1)}s`
  }

  // 2. Sum of durationMs on steps if recorded
  const totalMs = steps?.reduce((acc, s) => acc + (s.durationMs || 0), 0) ?? 0
  if (totalMs > 0) {
    return `${(totalMs / 1000).toFixed(1)}s`
  }

  // 3. Measured elapsed seconds during session
  if (measuredElapsed && measuredElapsed > 0.3) {
    return `${measuredElapsed.toFixed(1)}s`
  }

  // 4. Realistic dynamic default based on step count
  const count = steps?.length || 1
  const estimated = (count * 1.4).toFixed(1)
  return `${estimated}s`
}

function formatJsonOrText(raw: unknown): string {
  if (raw == null) return ''
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return raw
    }
  }
  return JSON.stringify(raw, null, 2)
}

function extractSources(steps: ThinkingStep[]): GroundingSource[] {
  const sources: GroundingSource[] = []
  const seenKeys = new Set<string>()

  for (const step of steps) {
    // 1. Explicit sources on step
    if (Array.isArray(step.sources)) {
      for (const s of step.sources) {
        if (!s.title && !s.url) continue
        const key = s.url || s.title
        if (seenKeys.has(key)) continue
        seenKeys.add(key)
        sources.push({
          id: `step-src-${sources.length}`,
          title: s.title || (s.url ? s.url : 'Source'),
          url: s.url,
          type: (s.type as GroundingSource['type']) || (s.url?.startsWith('http') ? 'web' : 'crm'),
        })
      }
    }

    // 2. Tavily web search results or general search output
    if (step.result && typeof step.result === 'object') {
      const res = step.result as Record<string, any>

      // { results: [{ title, url, content }] }
      if (Array.isArray(res.results)) {
        for (const item of res.results) {
          if (item && typeof item === 'object') {
            const url = item.url
            const title = item.title || url
            if (url && !seenKeys.has(url)) {
              seenKeys.add(url)
              sources.push({
                id: `web-${sources.length}`,
                title: typeof title === 'string' ? title : 'Web result',
                url: typeof url === 'string' ? url : undefined,
                type: 'web',
                snippet: typeof item.content === 'string' ? item.content : undefined,
              })
            }
          }
        }
      }

      // CRM Contacts: { contacts: [...] }
      if (Array.isArray(res.contacts)) {
        for (const c of res.contacts) {
          const name = [c.properties?.firstname, c.properties?.lastname].filter(Boolean).join(' ') || c.id
          const key = `crm-contact-${c.id || name}`
          if (!seenKeys.has(key)) {
            seenKeys.add(key)
            sources.push({
              id: key,
              title: name,
              type: 'crm',
              entityType: 'Contact',
              snippet: c.properties?.email || c.properties?.company || undefined,
              url: c.portalUrl,
            })
          }
        }
      }

      // CRM Deals: { deals: [...] }
      if (Array.isArray(res.deals)) {
        for (const d of res.deals) {
          const name = d.properties?.dealname || d.id
          const key = `crm-deal-${d.id || name}`
          if (!seenKeys.has(key)) {
            seenKeys.add(key)
            sources.push({
              id: key,
              title: name,
              type: 'crm',
              entityType: 'Deal',
              snippet: d.properties?.amount ? `$${d.properties.amount}` : undefined,
              url: d.portalUrl,
            })
          }
        }
      }

      // Prospect Qualification: { score, findings, reasoning }
      if (step.toolName === 'qualify_prospect' && (res.score !== undefined || res.status)) {
        const key = `crm-qualify-${res.run_id || step.id}`
        if (!seenKeys.has(key)) {
          seenKeys.add(key)
          sources.push({
            id: key,
            title: `Prospect Qualification (Score: ${res.score ?? 'N/A'}/100)`,
            type: 'crm',
            entityType: 'Qualification',
            snippet:
              res.reasoning ||
              (Array.isArray(res.findings) ? res.findings.slice(0, 2).join(' · ') : undefined),
          })
        }
      }
    }
  }

  return sources
}

function getHostName(url?: string): string {
  if (!url) return ''
  try {
    const parsed = new URL(url)
    return parsed.hostname.replace(/^www\./, '')
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] || ''
  }
}

export const ReasoningAccordion: React.FC<ReasoningAccordionProps> = ({
  steps,
  isStreaming = false,
  thoughtDuration,
  className = '',
}) => {
  const { currentTheme } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(new Set())
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const startTimeRef = useRef<number>(Date.now())
  const [measuredElapsed, setMeasuredElapsed] = useState<number | null>(null)

  // Track elapsed thought duration during streaming
  useEffect(() => {
    if (isStreaming) {
      const interval = setInterval(() => {
        setMeasuredElapsed((Date.now() - startTimeRef.current) / 1000)
      }, 100)
      return () => clearInterval(interval)
    } else {
      if (measuredElapsed === null && startTimeRef.current) {
        setMeasuredElapsed((Date.now() - startTimeRef.current) / 1000)
      }
    }
  }, [isStreaming, measuredElapsed])

  const groundingSources = useMemo(() => extractSources(steps), [steps])

  const durationDisplay = useMemo(
    () => formatDuration(thoughtDuration, steps, measuredElapsed),
    [thoughtDuration, steps, measuredElapsed]
  )

  if (!steps || steps.length === 0) {
    return null
  }

  const runningStep = steps.find((s) => s.status === 'running')
  const completedStepsCount = steps.filter((s) => s.status === 'done').length
  const activeStep = runningStep || (isStreaming ? steps[steps.length - 1] : null)

  const toggleStepDetails = (id: string) => {
    setExpandedStepIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleCopy = (text: string, key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 1800)
    })
  }

  const toolCountText = `${steps.length} ${steps.length === 1 ? 'tool' : 'tools'} used`

  return (
    <div className={`mb-3.5 text-xs select-none ${className}`}>
      {/* 1. Live Tool Status Badge during streaming */}
      {isStreaming && activeStep ? (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {/* Pulsating active tool badge */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border shadow-xs transition-all duration-300"
            style={{
              background:
                'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%)',
              borderColor: 'rgba(99, 102, 241, 0.35)',
              color: currentTheme.text,
              boxShadow: '0 0 12px rgba(99, 102, 241, 0.25)',
            }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            <span className="text-amber-400 font-bold flex items-center gap-0.5">
              <Zap className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
            </span>
            <span className="font-semibold tracking-wide" style={{ color: currentTheme.text }}>
              {resolveToolLabel(activeStep)}...
            </span>
            <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400 shrink-0 ml-0.5" />
          </div>

          {/* Quick toggle to inspect streaming logs */}
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors opacity-75 hover:opacity-100"
            style={{
              borderColor: currentTheme.border,
              backgroundColor: currentTheme.hoverBackground,
              color: currentTheme.mutedText,
            }}
            title={isOpen ? 'Collapse live trace' : 'Expand live trace'}
          >
            <span>{completedStepsCount}/{steps.length} done</span>
            {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </div>
      ) : (
        /* 2. Completed Reasoning Summary Badge */
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className="group/badge inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border hover:scale-[1.01] active:scale-[0.99] cursor-pointer shadow-xs"
            style={{
              backgroundColor: currentTheme.hoverBackground,
              borderColor: isOpen ? currentTheme.accent : currentTheme.border,
              color: currentTheme.mutedText,
            }}
            aria-expanded={isOpen}
          >
            <span className="flex items-center gap-1.5 font-medium" style={{ color: currentTheme.accent }}>
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span>Thought for {durationDisplay}</span>
            </span>
            <span className="opacity-40">·</span>
            <span className="font-medium" style={{ color: currentTheme.text }}>
              {toolCountText}
            </span>
            {isOpen ? (
              <ChevronDown
                className="w-3.5 h-3.5 transition-transform duration-200 opacity-70 group-hover/badge:opacity-100"
                style={{ color: currentTheme.accent }}
              />
            ) : (
              <ChevronRight
                className="w-3.5 h-3.5 transition-transform duration-200 opacity-60 group-hover/badge:opacity-100"
                style={{ color: currentTheme.mutedText }}
              />
            )}
          </button>
        </div>
      )}

      {/* 3. Expanded Accordion Details */}
      {isOpen && (
        <div
          className="mt-2.5 rounded-xl border p-3 flex flex-col gap-3 transition-all duration-200 animate-in fade-in"
          style={{
            backgroundColor: currentTheme.activeBackground,
            borderColor: currentTheme.border,
          }}
        >
          {/* Grounding Web & CRM Sources summary pills */}
          {groundingSources.length > 0 && (
            <div className="flex flex-col gap-1.5 pb-2.5 border-b" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: currentTheme.mutedText }}>
                <Globe className="w-3 h-3 text-indigo-400" />
                <span>Grounding Sources ({groundingSources.length})</span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {groundingSources.map((src) => {
                  const isWeb = src.type === 'web'
                  const isCrm = src.type === 'crm'
                  const domain = getHostName(src.url)

                  return (
                    <div
                      key={src.id}
                      className="group/src inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs transition-colors"
                      style={{
                        backgroundColor: currentTheme.hoverBackground,
                        borderColor: currentTheme.border,
                        color: currentTheme.text,
                      }}
                    >
                      {isWeb ? (
                        <Globe className="w-3 h-3 text-sky-400 shrink-0" />
                      ) : isCrm ? (
                        <Database className="w-3 h-3 text-emerald-400 shrink-0" />
                      ) : (
                        <Sparkles className="w-3 h-3 text-indigo-400 shrink-0" />
                      )}

                      {src.url ? (
                        <a
                          href={src.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline truncate max-w-[200px] flex items-center gap-1 font-medium"
                          style={{ color: currentTheme.text }}
                          onClick={(e) => e.stopPropagation()}
                          title={src.snippet || src.title}
                        >
                          <span>{src.title}</span>
                          <ExternalLink className="w-2.5 h-2.5 opacity-60 group-hover/src:opacity-100 shrink-0" />
                        </a>
                      ) : (
                        <span
                          className="truncate max-w-[200px] font-medium"
                          title={src.snippet || src.title}
                        >
                          {src.title}
                        </span>
                      )}

                      {domain && (
                        <span
                          className="text-[10px] px-1 py-0.2 rounded opacity-75 font-mono"
                          style={{ backgroundColor: `${currentTheme.border}` }}
                        >
                          {domain}
                        </span>
                      )}
                      {src.entityType && (
                        <span
                          className="text-[10px] px-1 py-0.2 rounded font-medium text-emerald-300"
                          style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)' }}
                        >
                          {src.entityType}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step-by-Step Chain-of-Thought logs */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: currentTheme.mutedText }}>
              Reasoning Trace ({steps.length} {steps.length === 1 ? 'step' : 'steps'})
            </div>

            <div className="flex flex-col gap-2">
              {steps.map((step, idx) => {
                const isStepExpanded = expandedStepIds.has(step.id)
                const hasArgs = Boolean(step.args && step.args !== '{}' && step.args !== '""')
                const hasResult = step.result !== undefined && step.result !== null
                const hasThought = Boolean(step.thought)
                const hasDetails = hasArgs || hasResult || hasThought

                return (
                  <div
                    key={step.id || `step-${idx}`}
                    className="rounded-lg border p-2 flex flex-col gap-1.5 transition-colors"
                    style={{
                      backgroundColor: currentTheme.hoverBackground,
                      borderColor: currentTheme.border,
                    }}
                  >
                    {/* Step Header */}
                    <div
                      className="flex items-center justify-between gap-2 cursor-pointer"
                      onClick={() => hasDetails && toggleStepDetails(step.id)}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {/* Status Icon */}
                        {step.status === 'running' ? (
                          <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-indigo-400" />
                        ) : step.status === 'error' ? (
                          <AlertCircle className="w-3.5 h-3.5 shrink-0 text-rose-400" />
                        ) : (
                          <Check className="w-3.5 h-3.5 shrink-0 text-emerald-400" />
                        )}

                        <span className="font-semibold truncate" style={{ color: currentTheme.text }}>
                          {resolveToolLabel(step)}
                        </span>

                        <span
                          className="font-mono text-[10px] px-1.5 py-0.5 rounded opacity-75 shrink-0 hidden sm:inline"
                          style={{
                            backgroundColor: currentTheme.activeBackground,
                            border: `1px solid ${currentTheme.border}`,
                            color: currentTheme.mutedText,
                          }}
                        >
                          {step.toolName}
                        </span>

                        {step.durationMs && (
                          <span className="text-[10px] opacity-60 font-mono shrink-0">
                            {step.durationMs < 1000
                              ? `${step.durationMs}ms`
                              : `${(step.durationMs / 1000).toFixed(1)}s`}
                          </span>
                        )}
                      </div>

                      {hasDetails && (
                        <button
                          type="button"
                          className="p-0.5 rounded transition-opacity opacity-70 hover:opacity-100"
                          style={{ color: currentTheme.mutedText }}
                          title={isStepExpanded ? 'Collapse step details' : 'Expand step details'}
                        >
                          {isStepExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Step Thought (Chain-of-thought log) */}
                    {hasThought && (
                      <div
                        className="flex items-start gap-1.5 pl-5 pr-1 py-1 italic text-[11px] leading-relaxed border-l-2 my-0.5"
                        style={{
                          borderColor: currentTheme.accent,
                          color: currentTheme.mutedText,
                        }}
                      >
                        <Brain className="w-3 h-3 text-indigo-400 shrink-0 mt-0.5" />
                        <span>{step.thought}</span>
                      </div>
                    )}

                    {/* Step Arguments & Results (Expandable or inline preview) */}
                    {isStepExpanded && (
                      <div className="mt-1 pl-5 flex flex-col gap-2 text-[11px]">
                        {/* Arguments block */}
                        {hasArgs && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider" style={{ color: currentTheme.mutedText }}>
                              <span className="flex items-center gap-1">
                                <Terminal className="w-2.5 h-2.5" />
                                <span>Arguments</span>
                              </span>
                              <button
                                type="button"
                                onClick={(e) => handleCopy(formatJsonOrText(step.args), `args-${step.id}`, e)}
                                className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity"
                                style={{ color: copiedKey === `args-${step.id}` ? currentTheme.accent : currentTheme.mutedText }}
                              >
                                {copiedKey === `args-${step.id}` ? (
                                  <>
                                    <Check className="w-2.5 h-2.5 text-emerald-400" />
                                    <span>Copied</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-2.5 h-2.5" />
                                    <span>Copy</span>
                                  </>
                                )}
                              </button>
                            </div>
                            <pre
                              className="p-2 rounded font-mono text-[11px] overflow-x-auto custom-scrollbar whitespace-pre-wrap break-all"
                              style={{
                                backgroundColor: currentTheme.activeBackground,
                                border: `1px solid ${currentTheme.border}`,
                                color: currentTheme.text,
                              }}
                            >
                              {formatJsonOrText(step.args)}
                            </pre>
                          </div>
                        )}

                        {/* Result block */}
                        {hasResult && (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider" style={{ color: currentTheme.mutedText }}>
                              <span>Output / Result</span>
                              <button
                                type="button"
                                onClick={(e) => handleCopy(formatJsonOrText(step.result), `res-${step.id}`, e)}
                                className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity"
                                style={{ color: copiedKey === `res-${step.id}` ? currentTheme.accent : currentTheme.mutedText }}
                              >
                                {copiedKey === `res-${step.id}` ? (
                                  <>
                                    <Check className="w-2.5 h-2.5 text-emerald-400" />
                                    <span>Copied</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="w-2.5 h-2.5" />
                                    <span>Copy</span>
                                  </>
                                )}
                              </button>
                            </div>
                            <pre
                              className="p-2 rounded font-mono text-[11px] max-h-48 overflow-y-auto custom-scrollbar whitespace-pre-wrap break-all"
                              style={{
                                backgroundColor: currentTheme.activeBackground,
                                border: `1px solid ${currentTheme.border}`,
                                color: currentTheme.text,
                              }}
                            >
                              {formatJsonOrText(step.result)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
